import React from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";

interface PromptStepProps {
  prompt: string;
  setPrompt: (value: string) => void;
  selectedTones: string[];
  setSelectedTones: (value: string[]) => void;
  persona: string;
  setPersona: (value: string) => void;
  seasonalTag: string;
  setSeasonalTag: (value: string) => void;
  offer: string;
  setOffer: (value: string) => void;
  ctaLabel: string;
  setCtaLabel: (value: string) => void;
  ctaLink: string;
  setCtaLink: (value: string) => void;
  onAutoCorrectChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (val: string) => void
  ) => void;
  onGenerate: () => void;
  loading: boolean;
}

export const PromptStep: React.FC<PromptStepProps> = ({
  prompt,
  setPrompt,
  selectedTones,
  setSelectedTones,
  setPersona,
  setSeasonalTag,
  setOffer,
  setCtaLabel,
  setCtaLink,
  onAutoCorrectChange,
  onGenerate,
  loading,
}) => {
  const { t } = useTranslation();
  const tones = [
    { value: "Friendly", label: t("chatbot.promptStep.tones.friendly") },
    { value: "Professional", label: t("chatbot.promptStep.tones.professional") },
    { value: "Urgent", label: t("chatbot.promptStep.tones.urgent") },
    { value: "Playful", label: t("chatbot.promptStep.tones.playful") },
    { value: "Informative", label: t("chatbot.promptStep.tones.informative") },
  ];

  return (
    <div className="promptbox-container w-[80%]">
      <p className="promptbox-subtitle">{t("chatbot.promptStep.title")}</p>

      <label className="promptbox-subtitle">
        {t("chatbot.promptStep.campaignGoalLabel")}
        <span className="required-star">*</span>
      </label>
      <textarea
        className="promptbox-textarea"
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder={t("chatbot.promptStep.campaignGoalPlaceholder")}
      />

      <div className="promptbox-tone-section">
        <h4 className="promptbox-subtitle">
          {t("chatbot.promptStep.tonePresets")} <span className="required-star">*</span>
        </h4>
        <div className="promptbox-tone-buttons">
          {tones.map((tone, idx) => (
            <button
              key={idx}
              type="button"
              className={`promptbox-tone-btn ${
                selectedTones.includes(tone.value) ? "active" : ""
              }`}
              onClick={() => {
                if (selectedTones.includes(tone.value)) {
                  setSelectedTones(selectedTones.filter(t => t !== tone.value));
                } else {
                  setSelectedTones([...selectedTones, tone.value]);
                }
              }}
            >
              {tone.label}
            </button>
          ))}
        </div>
      </div>

      <div className="promptbox-form-section">
        <label className="promptbox-subtitle">
          {t("chatbot.promptStep.personaLabel")} <span className="required-star">*</span>
        </label>
        <input
          type="text"
          className="promptbox-input"
          placeholder={t("chatbot.promptStep.personaPlaceholder")}
          onChange={e => onAutoCorrectChange(e, setPersona)}
        />
      </div>

      <div className="promptbox-form-section">
        <label className="promptbox-subtitle">
          {t("chatbot.promptStep.seasonalTagLabel")} <span className="required-star">*</span>
        </label>
        <input
          type="text"
          className="promptbox-input"
          placeholder={t("chatbot.promptStep.seasonalTagPlaceholder")}
          onChange={e => onAutoCorrectChange(e, setSeasonalTag)}
        />
      </div>

      <div className="promptbox-form-section">
        <label className="promptbox-subtitle">{t("chatbot.promptStep.offerLabel")}</label>
        <input
          type="text"
          className="promptbox-input"
          placeholder="20%"
          onChange={e => onAutoCorrectChange(e, setOffer)}
        />
      </div>

      <div className="promptbox-form-section">
        <label className="promptbox-subtitle">
          {t("chatbot.promptStep.ctaUrlLabel")} <span className="required-star">*</span>
        </label>
        <input
          type="text"
          className="promptbox-input"
          placeholder="https://"
          onChange={e => onAutoCorrectChange(e, setCtaLink)}
        />
      </div>

      <div className="promptbox-form-section">
        <label className="promptbox-subtitle">
          {t("chatbot.promptStep.ctaLabelLabel")} <span className="required-star">*</span>
        </label>
        <input
          type="text"
          className="promptbox-input"
          placeholder={t("chatbot.promptStep.ctaLabelPlaceholder")}
          onChange={e => onAutoCorrectChange(e, setCtaLabel)}
        />
      </div>

      <div className="category-actions">
        <button
          className="proceed-button"
          onClick={onGenerate}
          disabled={loading}
        >
          {t("chatbot.promptStep.generateWithAi")}
          {loading && (
            <span className="promptbox-loading">
              <Spin size="small" />
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
